import React from "react"
import {useTranslation} from "react-i18next"
import {toast} from "react-toastify"
import {BellIcon} from "@heroicons/react/24/outline"
import {twMerge} from "tailwind-merge"

import {useKlaraRemindMutation} from "../../../queries/klaraDashboard.ts"
import requestError from "../../../services/requestError.tsx"
import {getApiValidationMessages, isAxiosValidationError} from "../../../utils/validation.ts"
import {stopRowEvents} from "./stopRowEvents.ts"

/** A bell that notifies the lead's owner about the lead and reports the outcome in a toast. */
export const RemindButton: React.FC<{dealId: number}> = ({dealId}) => {
  const {t} = useTranslation()
  const refusalMessage = (code: string) => {
    switch (code) {
      case "no_channel":
        return t("KlaraDashboard_Leads_Remind_FailureToast_NoChannel")
      case "rate_limited":
        return t("KlaraDashboard_Leads_Remind_FailureToast_RateLimited")
      case "deal_closed":
        return t("KlaraDashboard_Leads_Remind_FailureToast_DealClosed")
      default:
        return t("KlaraDashboard_Leads_Remind_FailureToast_Generic")
    }
  }

  const {mutate, isPending} = useKlaraRemindMutation({
    onSuccess: () => toast.success(t("KlaraDashboard_Leads_Remind_SuccessToast")),
    onError: error => {
      if (!isAxiosValidationError(error)) {
        return requestError(error)
      }
      toast.error(getApiValidationMessages(error.response.data, "errors").map(refusalMessage).join(" "))
    },
  })

  return (
    <button
      type={"button"}
      // Not natively disabled: a disabled button would drop the keyboard focus while the reminder is sent.
      aria-disabled={isPending}
      aria-label={t("KlaraDashboard_Leads_Remind_Label")}
      title={t("KlaraDashboard_Leads_Remind_Label")}
      // Subtle until its row is hovered or it has keyboard focus.
      className={twMerge(
        "inline-flex size-7 items-center justify-center rounded-[7px] border border-cr-slate-15 bg-cr-white text-cr-slate-60 opacity-35 transition",
        "group-hover/row:border-cr-grey-15 group-hover/row:text-cr-slate-90 group-hover/row:opacity-100 hover:border-cr-blue-mid hover:text-cr-blue focus-visible:opacity-100",
        isPending && "cursor-wait opacity-50"
      )}
      onClick={event => {
        stopRowEvents.onClick(event)
        if (!isPending) {
          mutate(dealId)
        }
      }}
      onKeyDown={stopRowEvents.onKeyDown}
    >
      <BellIcon className={"size-3.5"} aria-hidden />
    </button>
  )
}
